import {
	Add,
	ArrowDown,
	ArrowUp,
	ArrowsVertical,
	DownToBottom,
	OverflowMenuVertical,
	Replicate,
	TrashCan,
	UpToTop,
} from "@carbon/icons-react";
import { type Row, type Table } from "@tanstack/react-table";
import { useCallback } from "react";

import { Button } from "../../Button";
import { DropdownMenu } from "../../DropdownMenu";
import { emptyRow } from "../useColumnState";

export interface RowControlsMenuProps<T> {
	table: Table<T>;
	row: Row<T>;
}

const disabledMessage = (
	<>
		{"To allow mutation, provde an "}
		<code>onDataValueChange</code>
		{" callback to the table"}
	</>
);

export function RowControlsMenu<T>({ table, row }: RowControlsMenuProps<T>) {
	const canMutateData = table.options.meta?.tableEditable;

	const insertRow = useCallback(
		(rowIndex: number, rowData?: T) => {
			const newRow = rowData ?? (emptyRow(table as Table<unknown>) as T);
			table.options.meta?.insertRow?.(rowIndex, newRow);
		},
		[table.options.meta?.insertRow]
	);
	const moveRowToIndex = table.options.meta?.moveRowToIndex;
	const deleteRow = table.options.meta?.deleteRow;

	return (
		<DropdownMenu.Root>
			<DropdownMenu.Trigger asChild>
				<Button
					className="!h-[43px] !w-6 !rounded-none !rounded-l !p-0"
					icon={OverflowMenuVertical}
					label=""
					size="sm"
					level="tertiary"
				/>
			</DropdownMenu.Trigger>
			<DropdownMenu.Portal>
				<DropdownMenu.Content align="end">
					{/* <DropdownMenu.Item>
						<Maximize />
						<div>Expand row</div>
					</DropdownMenu.Item>
					<DropdownMenu.Separator /> */}
					<DropdownMenu.Sub>
						<DropdownMenu.SubTrigger
							asChild={false}
							disabledMessage={disabledMessage}
							disabled={!canMutateData}
						>
							<Add />
							<div>Insert row</div>
						</DropdownMenu.SubTrigger>
						<DropdownMenu.Portal>
							<DropdownMenu.SubContent>
								<DropdownMenu.Item onSelect={() => insertRow?.(row.index)}>
									<ArrowUp />
									<div>Insert row above</div>
								</DropdownMenu.Item>
								<DropdownMenu.Item onSelect={() => insertRow?.(row.index + 1)}>
									<ArrowDown />
									<div>Insert row below</div>
								</DropdownMenu.Item>
							</DropdownMenu.SubContent>
						</DropdownMenu.Portal>
					</DropdownMenu.Sub>
					<DropdownMenu.Sub>
						<DropdownMenu.SubTrigger
							asChild={false}
							disabledMessage={disabledMessage}
							disabled={!canMutateData}
						>
							<ArrowsVertical />
							<div>Move row</div>
						</DropdownMenu.SubTrigger>
						<DropdownMenu.Portal>
							<DropdownMenu.SubContent>
								<DropdownMenu.Item
									onSelect={() => moveRowToIndex?.(row.index, row.index - 1)}
								>
									<ArrowUp />
									<div>Move row up</div>
								</DropdownMenu.Item>
								<DropdownMenu.Item
									onSelect={() => moveRowToIndex?.(row.index, row.index + 1)}
								>
									<ArrowDown />
									<div>Move row down</div>
								</DropdownMenu.Item>
								<DropdownMenu.Item onSelect={() => moveRowToIndex?.(row.index, 0)}>
									<UpToTop />
									<div>Move row to top</div>
								</DropdownMenu.Item>
								<DropdownMenu.Item
									onSelect={() => moveRowToIndex?.(row.index, "end")}
								>
									<DownToBottom />
									<div>Move row to bottom</div>
								</DropdownMenu.Item>
							</DropdownMenu.SubContent>
						</DropdownMenu.Portal>
					</DropdownMenu.Sub>
					<DropdownMenu.Item
						onSelect={() => insertRow?.(row.index + 1, row.original)}
						disabledMessage={disabledMessage}
						disabled={!canMutateData}
					>
						<Replicate />
						<div>Duplicate row below</div>
					</DropdownMenu.Item>

					<DropdownMenu.Separator />
					<DropdownMenu.Item
						variant="negative"
						onSelect={() => deleteRow?.(row.index)}
						disabledMessage={disabledMessage}
						disabled={!canMutateData}
					>
						<TrashCan />
						<div>Delete row</div>
					</DropdownMenu.Item>
				</DropdownMenu.Content>
			</DropdownMenu.Portal>
		</DropdownMenu.Root>
	);
}
